import { t, obtenerBiselado, obtenerPrecalibrado, obtenerTipoLente } from "../labelHelpers";
import { getPurpleGrid } from "../styles";
import type { PdfContext } from "../types";

// Columna derecha (65%): tabla con lente, diámetro, tratamientos,
// coloración/fotocromático, base especial, descentramiento, prisma, tipo de
// lente, biselado/montaje y precalibrado/espesor especial.
export function buildRightColumn(ctx: PdfContext) {
  const lg = ctx.labelGetters;

  // Tipo de lente: `SERVICIO` lo decide SAP al calcular. Sin cálculo solo se
  // puede afirmar "stock" si el usuario marcó el check de stock en el
  // formulario; si no lo marcó, el tipo sigue siendo desconocido y se deja en
  // blanco en vez de arriesgar un "laboratorio" que el cálculo puede desmentir.
  //
  // El check "Solo stock" es del pedido, no de cada ojo: la casilla solo se
  // pinta en el OD (en el OI ese hueco lo ocupa "Copiar OD"), así que el mismo
  // valor vale para las dos celdas.
  const stockFormulario = Boolean(ctx.pedidosStore.od?.stock || ctx.pedidosStore.oi?.stock);
  const tipoLente = (ojoOut: any): string =>
    obtenerTipoLente(ojoOut?.SERVICIO) || (stockFormulario ? t("stock") : "");
  return {
                    /* -------- COLUMNA DERECHA ------------------------------------ */
                    width: "65%",
                    margin: [8, 6, 0, 8],
                    table: {
                      widths: ["*", "*", "*"],
                      body: [
                        [
                          { text: "", border: [false, false, false, false] },
                          {
                            text: t("ojo_derecho"),
                            style: "th",
                            border: [false, false, false, false],
                          },
                          {
                            text: t("ojo_izquierdo"),
                            style: "th",
                            border: [false, false, false, false],
                          },
                        ],
                        [
                          { text: t("lente"), border: [false, false, false, false] },
                          {
                            text: ctx.datosAdicionalesderecha.nombrelente || ctx.store?.state?.pedidos?.caracteristicasLente?.Nombre || "",
                            style: "th",
                            border: [true, true, true, true],
                          },
                          {
                            text: ctx.datosAdicionalesizquierda.nombrelente || ctx.store?.state?.pedidos?.caracteristicasLente?.Nombre || "",
                            style: "th",
                            border: [true, true, true, true],
                          },
                          /*ctx.pedido?.oClaseBAPI?.datosRetornoDerecho?.lente /  ctx.pedido?.oClaseBAPI?.datosRetornoIzquierdo?.lente  */
                        ],
                        [
                          { text: t("diametro"), border: [false, false, false, false] },
                          {
                            text: `${ctx.pedido?.oClaseBAPI?.ojoOutDerecho?.DIAMETRO ?? ctx.store?.state?.pedidos?.od?.diametro ?? ctx.store?.state?.pedidos?.od?.diametroEspecial ?? ""}`,
                            style: "th",
                            border: [true, true, true, true],
                          },
                          {
                            text: `${ctx.pedido?.oClaseBAPI?.ojoOutIzquierdo?.DIAMETRO ?? ctx.store?.state?.pedidos?.oi?.diametro ?? ctx.store?.state?.pedidos?.oi?.diametroEspecial ?? ""}`,
                            style: "th",
                            border: [true, true, true, true],
                          },
                          /*ctx.pedido?.oClaseBAPI?.ojoOutDerecho?.DIAMETRO /  ctx.pedido?.oClaseBAPI?.ojoOutIzquierdo?.DIAMETRO  */
                        ],
                        [
                          { text: t("tratamientos"), border: [false, false, false, false] },
                          {
                            text: (() => {
                              const result = ctx.datosAdicionalesderecha.tratamiento || lg.getTratamientoLabel() || "";
                              return result;
                            })(),
                            style: "th",
                            border: [true, true, true, true],
                          },
                          {
                            text: ctx.datosAdicionalesizquierda.tratamiento || lg.getTratamientoLabel() || "",
                            style: "th",
                            border: [true, true, true, true],
                          },
                          /*ctx.pedido?.oClaseBAPI?.datosRetornoDerecho?.tratamiento /  ctx.pedido?.oClaseBAPI?.datosRetornoIzquierdo?.tratamiento  */
                        ],
                        [
                          {
                            text: `${t("coloracion")}/\n ${t("fotocromatico")}`,
                            border: [false, false, false, false],
                          },
                          {
                            text: (() => {

                              // Obtener fotocromático
                              const fotocromatico = ctx.datosAdicionalesderecha.fotocromatico || lg.getFotocromaticoLabel();

                              // Combinar Coloración + Color + Tono (igual que en obtenerDatosAdicionales línea 262)
                              let coloracionCombinada = "";
                              if (ctx.datosAdicionalesderecha.coloracion) {
                                // Si viene de la API, usarlo directamente (ya viene combinado)
                                coloracionCombinada = ctx.datosAdicionalesderecha.coloracion;
                              } else {
                                // Si no viene de la API, combinar desde el store con separador "/"
                                const labelColoracion = lg.getColoracionLabel();
                                const labelColor = lg.getColorLabel();
                                const labelTono = lg.getTonoLabel();
                                coloracionCombinada = [labelColoracion, labelColor, labelTono]
                                  .filter(Boolean)
                                  .join(" / ");
                              }


                              const result = [fotocromatico, coloracionCombinada]
                                .filter(Boolean)
                                .join(" / ");

                              return result;
                            })(),
                            style: "ty",
                            border: [true, true, true, true],
                            colSpan: 2,
                          },
                          {} /*ctx.pedido?.oClaseBAPI?.datosRetornoDerecho?.coloracion /  ctx.pedido?.oClaseBAPI?.datosRetornoDerecho?.fotocromatico  */,
                        ],
                        [
                          { text: " ", border: [false, false, false, false], fontSize: 1 },
                          { text: "", border: [false, false, false, false], fontSize: 1 },
                          { text: "", border: [false, false, false, false], fontSize: 1 },
                        ],
                        [
                          { text: " ", border: [false, false, false, false], fontSize: 1 },
                          { text: "", border: [false, false, false, false], fontSize: 1 },
                          { text: "", border: [false, false, false, false], fontSize: 1 },
                        ],
                        [
                          { text: t("base_especial"), border: [false, false, false, false] },
                          {
                            text: ctx.pedido?.oClaseBAPI?.ojoInDerecho?.BASE || lg.getBaseEspecialODLabel() || "",
                            style: "th",
                            border: [true, true, true, true],
                          },
                          {
                            text: ctx.pedido?.oClaseBAPI?.ojoInIzquierdo?.BASE || lg.getBaseEspecialOILabel() || "",
                            style: "th",
                            border: [true, true, true, true],
                          },
                        ],
                        [
                          { text: t("descentramiento"), border: [false, false, false, false] },
                          {
                            text: ctx.pedido?.oClaseBAPI?.ojoInDerecho?.COTA_DESN || lg.getDescentramientoOD() || "",
                            style: "th",
                            border: [true, true, true, true],
                          },
                          {
                            text: ctx.pedido?.oClaseBAPI?.ojoInIzquierdo?.COTA_DESN || lg.getDescentramientoOI() || "",
                            style: "th",
                            border: [true, true, true, true],
                          },
                        ],
                        [
                          { text: t("prisma"), border: [false, false, false, false] },
                          {
                            text: ctx.datosAdicionalesderecha.descripcionprisma || lg.getPrismaODLabel() || "",
                            style: "th",
                            border: [true, true, true, true],
                          },
                          {
                            text: ctx.datosAdicionalesizquierda.descripcionprisma || lg.getPrismaOILabel() || "",
                            style: "th",
                            border: [true, true, true, true],
                          },
                        ],
                        [
                          { text: " ", border: [false, false, false, false], fontSize: 1 },
                          { text: "", border: [false, false, false, false], fontSize: 1 },
                          { text: "", border: [false, false, false, false], fontSize: 1 },
                        ],
                        [
                          { text: " ", border: [false, false, false, false], fontSize: 1 },
                          { text: "", border: [false, false, false, false], fontSize: 1 },
                          { text: "", border: [false, false, false, false], fontSize: 1 },
                        ],
                        [
                          { text: t("tipo_de_lente"), border: [false, false, false, false] },
                          {
                            text: tipoLente(ctx.pedido?.oClaseBAPI?.ojoOutDerecho),
                            style: "th",
                            border: [true, true, true, true],
                          },
                          {
                            text: tipoLente(ctx.pedido?.oClaseBAPI?.ojoOutIzquierdo),
                            style: "th",
                            border: [true, true, true, true],
                          },
                        ],
                        [
                          { text: " ", border: [false, false, false, false], fontSize: 1 },
                          { text: "", border: [false, false, false, false], fontSize: 1 },
                          { text: "", border: [false, false, false, false], fontSize: 1 },
                        ],
                        [
                          { text: " ", border: [false, false, false, false], fontSize: 1 },
                          { text: "", border: [false, false, false, false], fontSize: 1 },
                          { text: "", border: [false, false, false, false], fontSize: 1 },
                        ],
                        [
                          {
                            text: t("biselado_remotomontaje"),
                            border: [false, false, false, false],
                          },
                          {
                            text: `${
                              obtenerBiselado(
                                ctx.pedido?.oClaseBAPI?.CABIN?.BISEL,
                                ctx.pedido?.oClaseBAPI?.CABIN?.MONTAJE
                              ) || lg.getBiseladoMontajeLabel() || ""
                            }`,
                            style: "ty",
                            border: [true, true, true, true],
                          },
                          {
                            text: `${
                              obtenerBiselado(
                                ctx.pedido?.oClaseBAPI?.CABIN?.BISEL,
                                ctx.pedido?.oClaseBAPI?.CABIN?.MONTAJE
                              ) || lg.getBiseladoMontajeLabel() || ""
                            }`,
                            style: "ty",
                            border: [true, true, true, true],
                          },
                        ],
                        [
                          {
                            text: t("precablibradoespesor_especial"),
                            border: [false, false, false, false],
                          },
                          {
                            text: `${
                              obtenerPrecalibrado(
                                ctx.pedido?.oClaseBAPI?.ojoInIzquierdo?.PRECALIBRADO,
                                ctx.pedido?.oClaseBAPI?.ojoInIzquierdo?.ESP_BORDE,
                                ctx.pedido?.oClaseBAPI?.ojoInIzquierdo?.ESP_CENTRO
                              ) ||lg.getPrecalibradoLabel() ||""
                            }`,
                            style: "ty",
                            border: [true, true, true, true],
                          },
                          {
                            text: `${
                              obtenerPrecalibrado(
                                ctx.pedido?.oClaseBAPI?.ojoInIzquierdo?.PRECALIBRADO,
                                ctx.pedido?.oClaseBAPI?.ojoInIzquierdo?.ESP_BORDE,
                                ctx.pedido?.oClaseBAPI?.ojoInIzquierdo?.ESP_CENTRO
                              ) || lg.getPrecalibradoLabel() || ""
                            }`,
                            style: "ty",
                            border: [true, true, true, true],
                          },
                        ],
                      ],
                    },
                    layout: getPurpleGrid(),
  };
}
